<template>
	<div
		class="input-field search-field"
		:class="{
			readonly,
			highlighted: hasError
		}"
	>
		<input
			ref="input"
			type="text"
			required
			autocomplete="off"
			:value="currentValue"
			:maxlength="maxLength"
			:placeholder="placeholder"
			:readonly="readonly"
			@input.prevent="handleInput"
			@focus="$emit('input-focused')"
			@blur="$emit('input-blurred')"
		/>
		<div
			v-if="highlighterText"
			class="search-highlighter"
			v-html="highlighterText"
		/>
		<div
			v-if="currentValue || isShowCloseButtonByDefault"
			class="search-close-icon"
			@click="clearSearch"
		></div>
	</div>
</template>

<script>
export default {
	name: 'SearchField',

	props: {
		value: {
			type: String,
			default: ''
		},

		placeholder: {
			type: String
		},

		maxLength: {
			type: Number,
			default: 30
		},

		readonly: {
			type: Boolean,
			default: false
		},

		hasError: {
			type: Boolean,
			default: false
		},

		searchValueHighlighted: {
			type: String,
			default: ''
		},

		isShowCloseButtonByDefault: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			currentValue: ''
		}
	},

	created() {
		this.currentValue = this.value || ''
	},

	computed: {
		highlighterText() {
			return (
				this.searchValueHighlighted ||
				this.currentValue.replace(/</g, '&lt;').replace(/>/g, '&gt;')
			)
		}
	},

	watch: {
		value(val) {
			this.currentValue = val || ''
		}
	},

	methods: {
		focusInput() {
			if (this.$refs && this.$refs.input) {
				this.$refs.input.focus()
			}
		},

		clearSearch() {
			this.$emit('input', '')
			this.$emit('clear')
		},

		handleInput(e) {
			this.currentValue = e.target.value
			const cursorPosition = e.target.selectionStart

			const { currentValue } = this
			const { length } = currentValue

			if (
				currentValue &&
				this.searchValueHighlighted &&
				cursorPosition === length &&
				this.value.length < length
			) {
				const lastPart = currentValue[length - 1]
				const firstPart = currentValue.substring(0, length - 2)

				this.currentValue = `${firstPart}${lastPart}`
			}

			this.$emit('input', this.currentValue)
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.input-field.search-field {
	position: relative;
	margin-bottom: 0;
	border: none;
	.search-highlighter {
		/deep/ .highlighted-text {
			background-color: #e9576a;
		}
	}

	.search-close-icon {
		position: absolute;
		right: 3px;
		top: 50%;
		transform: translateY(-50%);
		width: 20px;
		height: 20px;
		cursor: pointer;
		z-index: 3;
		opacity: 1;
		transition: opacity 0.3s;

		&:hover {
			opacity: 0.7;
		}

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			width: 16px;
			height: 1px;
			background: $white;
			opacity: 1;
			border-radius: 2px;
		}

		&::before {
			transform: translate(-50%, -50%) rotate(45deg);
		}

		&::after {
			transform: translate(-50%, -50%) rotate(-45deg);
		}
	}

	&.highlighted {
		border-color: $alertRed;

		&:after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: -1px;
			height: 1px;
			overflow: hidden;
			transform: scale(0.1);
			animation: input-pulse-red 1.1s 3;
		}
	}

	&.readonly {
		pointer-events: none;
	}

	input {
		position: relative;
		padding-right: 24px;
		z-index: 1;

		&::placeholder {
			font-size: 14px;
		}
	}

	.search-highlighter {
		position: absolute;
		left: 5px;
		bottom: 10px;
		z-index: 0;
	}
}
</style>
